import * as html from 'smg/html';

import { State } from '../../state';
import { Payment } from '../../types';
import { BillingAddressForm } from '../billing-address';

// SetupBillingAddress replaces any existing BillingAddressForm on
// state.payment with a fresh one scoped to $el, preserving prior address
// and toggle state. Used by CC/bank/PO forms.
export function SetupBillingAddress(state: State, $el: HTMLElement): void {
    const p = state.payment;
    if (p.billingAddress) {
        const prevAddress = p.billingAddress.get();
        // `prevSame` can be `false` when the user explicitly entered a custom
        // billing address. Preserve that choice — only default to "same as
        // shipping" when it was never set.
        const prevSame = p.billingShippingSame ?? true;
        p.billingAddress = new BillingAddressForm(state, $el);
        if (p.billingAddress.$addrSameToggle) {
            p.billingAddress.$addrSameToggle.checked = prevSame;
            // Bootstrap's collapse reads from the toggle's target; click to
            // expand the billing form when same-as-shipping is unchecked.
            if (!prevSame) {
                p.billingAddress.$addrSameToggle.dispatchEvent(new Event('click'));
            }
        }
        p.billingAddress.set(prevAddress);
    } else {
        p.billingAddress = new BillingAddressForm(state, $el);
    }
    BindAddressSameToggle(state);
}

// SetupBillingAddressWithToggle is the check/wire variant — click()s the
// toggle off so the form reveals the previous address.
export function SetupBillingAddressWithToggle(state: State, $el: HTMLElement): void {
    const p = state.payment;
    if (p.billingAddress) {
        const prevAddress = p.billingAddress.get();
        const prevSame = p.billingShippingSame ?? true;
        p.billingAddress = new BillingAddressForm(state, $el);
        setTimeout(() => {
            if (!prevSame) {
                p.billingAddress?.$addrSameToggle?.click();
                p.billingAddress?.set(prevAddress);
            }
        });
    } else {
        p.billingAddress = new BillingAddressForm(state, $el);
    }
    BindAddressSameToggle(state);
}

export function BindAddressSameToggle(state: State): void {
    const p = state.payment;
    if (!p.billingAddress?.$addrSameToggle) {
        return;
    }
    p.billingAddress.$addrSameToggle.addEventListener('click', () => {
        p.billingShippingSame = p.billingAddress?.$addrSameToggle?.checked || false;
    });
}

export function PromoteFormBodyIDs($el: HTMLElement): void {
    const $formBody = $el.querySelector<HTMLDivElement>('.payment-option-form-body');
    if ($formBody) {
        html.IDsToClasses($formBody);
    }
}

export function BindBackLink($el: HTMLElement, back: () => void): void {
    const $back = $el.querySelector<HTMLButtonElement>('#payment-option-back');
    if (back) {
        $back?.addEventListener('click', back);
    } else {
        $back?.classList.add('d-none');
    }
}

export function EnsureCreditCard(p: Payment): NonNullable<Payment['creditCard']> {
    if (!p.creditCard) {
        p.creditCard = { creditCardNumber: '', expireMonth: 0, expireYear: 0, securityCode: '' };
    }
    return p.creditCard;
}

export function EnsurePurchaseOrder(p: Payment): NonNullable<Payment['purchaseOrder']> {
    if (!p.purchaseOrder) {
        p.purchaseOrder = { purchaseOrderNumber: '', purchaseOrderUploadIDs: [] };
    }
    return p.purchaseOrder;
}
